import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../lib/icons';

/** Ctrl+K on Windows/Linux, ⌘K on Mac — the de-facto standard for "search from
 *  anywhere". Rendered in the label so the button teaches the shortcut. */
export const SEARCH_SHORTCUT_LABEL = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  ? '⌘K'
  : 'Ctrl K';

/** Typing in a field, an editor or a contenteditable must never be hijacked —
 *  ⌘K is a link shortcut in several rich-text editors. */
function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node || typeof node.closest !== 'function') return false;
  return !!node.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
}

/**
 * The global search entry point: a magnifier in the topbar's right cluster,
 * immediately left of the notifications bell, plus the keyboard shortcut that
 * opens the same workspace from any page. (f-01826)
 *
 * Both routes go to `/search`, which owns the query in its URL — so a result set
 * is shareable and Back returns you to whatever you were reading.
 */
export function SearchLauncher() {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'k' && e.key !== 'K') return;
      if (!e.metaKey && !e.ctrlKey) return;
      if (e.altKey || e.shiftKey) return;
      // Inside an editor the browser/editor binding wins; the button is still there.
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      navigate('/search');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <button
      type="button"
      className="btn btn-icon"
      aria-label={`Search (${SEARCH_SHORTCUT_LABEL})`}
      title={`Search — ${SEARCH_SHORTCUT_LABEL}`}
      onClick={() => navigate('/search')}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer',
      }}
    >
      <Icon name="search" size={18} />
    </button>
  );
}
